import GhHtmlElement from '@gudhub/gh-html-element';
import html from './usersSideBar.html';
import './usersSideBarItem/usersSideBarItem.js';
import './itemSideBarItem/itemSideBarItem.js';
import './channelSideBarItem/channelSideBarItem.js';
import { GUDHUB_SETTINGS } from '../../../gudhub_settings.js';
import IconGenerator from '../../../gui/gh_icon/icons_generator.js';

// Sections render at most this many rows up-front; the rest are revealed by
// the "Show N more" button (see renderCappedList).
const LIST_LIMIT = 15;

const SEARCH_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>`;

// Rendered for the "Item Discussions" app-group header icon when an app has
// no icon of its own - matches invitation_action.js's own fallback for the
// same "app has no icon set" case in its create-group dialog.
const FALLBACK_APP_ICON = { icon_id: 'lego', icon_color: '0893d2' };
// Matches --sidebar-avatar-size (style.scss) - kept as a literal like every
// other gh-icon call in the codebase (see invitation_action.js/gh_application.js),
// since IconGenerator bakes the size into the generated SVG's own attributes
// rather than reading a CSS custom property.
const APP_ICON_SIZE = '22px';

const SHOW_MORE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path d="M6 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm12 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/></svg>`;

// Default permission granted when adding an existing user to a group (see
// gudhub.groupSharing). Group admin/owner gating for the "+Add" button itself
// lives in usersSideBar.html (group.group_permission == 2 || == 3), matching
// membersTable.directive.js's own checks.
const GROUP_MEMBER_PERMISSION = 1;

// Same validation used by the invitation constructor (invitation_action.js) -
// kept in sync so a query that would be accepted there is also accepted here.
const EMAIL_REGEXP = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// gudhub.getUsersList() (see gui/gh_input/autocomplete.js) only searches once
// the query is at least this long.
const SEARCH_MIN_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

class GhUsersSideBar extends GhHtmlElement {

    constructor() {
        super();
        this.handleEmitToUser = this.handleEmitToUser.bind(this);
    }

    async onInit() {
        this.activeUserId = gudhub.storage.user.user_id;
        this.searchIcon = SEARCH_ICON;

        const conversationsElement = document.querySelector('gudhub-messenger');
        this.hasMessengers = conversationsElement.hasMessengers;
        this.messengerConfigs = this.hasMessengers ? this.getMessengerConfigs(conversationsElement.model.data_model.messengers) : [];
        this.activeView = this.hasMessengers ? 'messengers' : 'activity';
        this.activeMessengerIndex = 0;
        this.itemContext = this.closest('gudhub-messenger')?.itemContext || null;

        let conversations, apps;

        [this.groupedUsers, conversations, this.itemConversations, apps] = await Promise.all([
            this.getGroupedUsers(),
            this.getConversationsList(),
            this.getItemConversations(),
            gudhub.getAppsList()
        ]);

        this.appNamesById = new Map((apps || []).map(app => [String(app.app_id), app.app_name]));

        this.activeConversations = this.getActiveConversations(conversations);
        this.channels = this.getChannels(conversations);

        this.activeConversationsHtml = this.renderCappedList(this.activeConversations, conversation => `
            <gh-user-side-bar-item class="users_sidebar__user" user-id="${conversation.user_id}" unread-count="${conversation.unread_count}"></gh-user-side-bar-item>
        `);
        this.channelsHtml = this.renderCappedList(this.channels, channel => this.renderChannel(channel));
        this.itemsByApp = this.buildItemsByApp(this.itemConversations);
        await Promise.all(this.itemsByApp.map(async group => {
            group.app_icon = await this.renderAppIcon(group.app_id);
        }));

        this.groupedUsers.forEach(group => {
            group.usersHtml = group.users === null ? null : this.renderUsers(group.users);
        });

        super.render(html);
        this.wireDiscussedItems();
        this.wireListControls();
        this.highlightCurrentItem(this.itemContext);

        if(this.itemContext) {
            await this.applyItemDiscussionState(this.itemContext);
        } else if(!this.hasMessengers && !window.matchMedia('(max-width: 767px)').matches) {
            this.dispatchEvent(new CustomEvent('switch_chat_view', {
                bubbles: true,
                detail: { view: 'activity' }
            }));
        }

        this.syncHeight();
        this.heightObserver = new ResizeObserver(() => this.syncHeight());
        this.heightObserver.observe(this.closest('.conversation_layout').querySelector('.multi-chat'));

        this.subscribeToConversations();
    }

    // Keep the sidebar's height in sync with the chat column, so its own
    // content scrolls internally instead of growing the whole layout.
    syncHeight() {
        if(window.matchMedia('(max-width: 767px)').matches) {
            this.style.height = '';
            return;
        }
        const multiChat = this.closest('.conversation_layout')?.querySelector('.multi-chat');
        if(!multiChat) return;
        this.style.height = `${multiChat.getBoundingClientRect().height}px`;
    }

    onDestroy() {
        this.heightObserver?.disconnect();
        this.unsubscribeFromConversations();
    }

    // Only the first group is unfolded by default, so its users are loaded upfront.
    // Other groups load their users lazily, when unfolded (see toggleGroup).
    async getGroupedUsers() {
        const groups = await gudhub.groupSharing.getGroupsByUser(this.activeUserId);

        if(!groups || !groups.length) return [];

        const groupedUsers = groups.map(group => ({
            group_id: group.group_id,
            group_name: group.group_name,
            group_permission: group.group_permission,
            users: null
        }));

        groupedUsers[0].users = await this.loadGroupUsers(groupedUsers[0].group_id);

        return groupedUsers.filter((group, index) => index !== 0 || group.users.length);
    }

    //********************* LOAD GROUP USERS ********************************//
    // Fetches a group's members with their display names and sorts them
    // alphabetically (groups have no per-member "last active" timestamp to
    // sort by). gudhub.getUserById caches by user_id, so the subsequent
    // gh-user-side-bar-item.connectedCallback()'s own lookup for the same
    // user is served from cache - and gh_conversation.js's one-time
    // getUserWorkspace() prefetch already warms that cache for whichever
    // members are in the caller's workspace. Not computed from that
    // workspace list directly: sharing groups routinely include people
    // outside it (e.g. clients in another company) - see contacts.js's
    // getOrgContacts for the measurement that ruled this out.
    // * groupId (string)
    async loadGroupUsers(groupId) {
        const groupUsers = await gudhub.groupSharing.getUsersByGroup(groupId);

        if(!groupUsers) return [];

        const usersWithNames = await Promise.all(groupUsers.map(async user => ({
            ...user,
            fullname: (await gudhub.getUserById(user.user_id))?.fullname || ''
        })));

        return usersWithNames.sort((a, b) => a.fullname.localeCompare(b.fullname));
    }

    //********************* GET MESSENGER CONFIGS ********************************//
    // Builds the display data (label + icon) for each configured external
    // messenger, for rendering as rows in the "Messengers" sidebar group.
    // * messengers (Array<Object>) — model.data_model.messengers
    getMessengerConfigs(messengers) {
        return messengers.map(messenger => ({
            name: messenger.messenger_name,
            label: `${messenger.messenger_name}${messenger.messenger_settings.page_name ? ' - ' + messenger.messenger_settings.page_name : ''}`,
            icon: this.getMessengerIcon(messenger.messenger_name)
        }));
    }

    //********************* GET MESSENGER ICON ********************************//
    // Returns the <img> markup for a messenger's icon, matching the icons
    // shown on message bubbles in gh-chat.
    // * messengerName (string) — e.g. 'slack', 'telegram', 'claude'
    getMessengerIcon(messengerName) {
        const icons = {
            telegram: 'telegram.svg',
            viber: 'viber.svg',
            facebook: 'facebook.svg',
            slack: 'slack.svg',
            claude: 'claude.svg'
        };

        const icon = icons[messengerName];

        return icon ? `<img src="https://gudhub.com/modules/conversation/public/images/${icon}" alt="${messengerName}">` : '';
    }

    //********************* GET CONVERSATIONS LIST ********************************//
    // Fetches the active user's top-level conversations (DMs/channels), shared
    // by getActiveConversations() and getChannels() to avoid duplicate requests.
    async getConversationsList() {
        const response = await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/list?user_id=${this.activeUserId}`);
        return await response.json();
    }

    //********************* GET ACTIVE CONVERSATIONS ********************************//
    // Returns everyone the active user already has a direct GudHub Messenger
    // conversation with, along with that conversation's unread_count for
    // rendering the sidebar's unread badges.
    // * conversations (Array<Object>) — from getConversationsList()
    getActiveConversations(conversations) {
        return conversations
            .filter(conversation => conversation.type === 'direct')
            .map(conversation => ({
                user_id: conversation.participants.map(String).find(id => id !== String(this.activeUserId)),
                unread_count: conversation.unread_count || 0
            }))
            .filter(conversation => conversation.user_id);
    }

    //********************* GET CHANNELS ********************************//
    // Returns the named, multi-member "channel" conversations the active user
    // participates in, for the sidebar's "Channels" section.
    // * conversations (Array<Object>) — from getConversationsList()
    getChannels(conversations) {
        return conversations
            .filter(conversation => conversation.type === 'channel')
            .map(conversation => ({
                conversation_id: conversation.conversation_id,
                name: conversation.name,
                unread_count: conversation.unread_count || 0,
                participants: conversation.participants.map(String)
            }));
    }

    //********************* GET ITEM CONVERSATIONS ********************************//
    // Returns the item-threads the active user participates in, for the
    // sidebar's "Item Discussions" section.
    async getItemConversations() {
        const response = await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/item/list?user_id=${this.activeUserId}`);
        return await response.json(); // [{ conversation_id, item_ref, unread_count }]
    }

    //********************* RENDER ITEM DISCUSSION ********************************//
    // Markup for a single "Item Discussions" row, shared by the initial render
    // and handleEmitToUser's insertAdjacentHTML.
    // * item (Object) — { conversation_id, item_ref, unread_count }
    renderItemDiscussion(item) {
        return `<gh-item-side-bar-item class="users_sidebar__user" conversation-id="${item.conversation_id}" app-id="${item.item_ref.app_id}" item-id="${item.item_ref.item_id}" view-id="${item.item_ref.view_id || ''}" unread-count="${item.unread_count}"></gh-item-side-bar-item>`;
    }

    //********************* BUILD ITEMS BY APP ********************************//
    // Groups the "Item Discussions" list by app, sorted alphabetically by app
    // name (same convention as the app pickers in invitation_action.js and
    // agent_constructor_action.js), each group's rows capped exactly like a
    // shared-users group (see renderUsers/renderCappedList).
    // * items (Array<Object>) — [{ conversation_id, item_ref, unread_count }]
    // Returns Array<{ app_id, app_name, items, itemsHtml }>
    buildItemsByApp(items) {
        const groups = new Map();

        items.forEach(item => {
            const appId = String(item.item_ref.app_id);
            if(!groups.has(appId)) groups.set(appId, { app_id: appId, app_name: this.getAppName(appId), items: [] });
            groups.get(appId).items.push(item);
        });

        return [...groups.values()]
            .sort((a, b) => a.app_name.localeCompare(b.app_name))
            .map(group => ({ ...group, itemsHtml: this.renderCappedList(group.items, item => this.renderItemDiscussion(item)) }));
    }

    //********************* GET APP NAME ********************************//
    // * appId (string) — falls back to the raw id (e.g. an app the user lost
    //   access to since the initial gudhub.getAppsList() snapshot).
    getAppName(appId) {
        return this.appNamesById.get(String(appId)) || String(appId);
    }

    //********************* RENDER APP ICON ********************************//
    // SVG markup for an "Item Discussions" app-group header icon. Calls
    // IconGenerator directly (the plain JS module gh-icon's Angular directive
    // itself delegates to - see gui/gh_icon/gh_icon.js) since this component
    // renders its own HTML strings rather than compiled Angular templates.
    // Falls back to FALLBACK_APP_ICON, matching the same "app has no icon"
    // fallback used by invitation_action.js's create-group dialog.
    // * appId (string)
    async renderAppIcon(appId) {
        let info;
        try {
            info = await gudhub.appProcessor.getAppInfo(appId);
        } catch(_) {
            info = null;
        }

        const icon = info?.icon || FALLBACK_APP_ICON;
        return IconGenerator.getIcon(icon.icon_id, String(icon.icon_color || '').replace('#', ''), APP_ICON_SIZE, 'app', icon.gradient_up, icon.gradient_down);
    }

    //********************* RENDER ITEM APP GROUP ********************************//
    // Markup for one collapsible per-app group in "Item Discussions", reusing
    // the "Groups" section's own CSS (see toggleItemAppGroup) rather than
    // adding a parallel set of classes.
    // * appGroup (Object) — { app_id, app_name, app_icon, itemsHtml }
    // * collapsed (boolean)
    renderItemAppGroup(appGroup, collapsed) {
        return `
            <div class="users_sidebar__group ${collapsed ? 'users_sidebar__group--collapsed' : ''}" data-app-id="${appGroup.app_id}">
                <div class="users_sidebar__group_name">
                    <div class="users_sidebar__group_label">
                        <svg class="users_sidebar__group_arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z"/></svg>
                        <span class="users_sidebar__group_icon">${appGroup.app_icon}</span>
                        <span>${appGroup.app_name}</span>
                    </div>
                </div>
                <div class="users_sidebar__group_users">
                    ${appGroup.itemsHtml}
                </div>
            </div>
        `;
    }

    //********************* RENDER CHANNEL ********************************//
    // Markup for a single "Channels" row, shared by the initial render and
    // handleEmitToUser/addChannel's insertAdjacentHTML.
    // * channel (Object) — { conversation_id, name, unread_count }
    renderChannel(channel) {
        return `<gh-channel-side-bar-item class="users_sidebar__user" conversation-id="${channel.conversation_id}" name="${channel.name}" unread-count="${channel.unread_count}"></gh-channel-side-bar-item>`;
    }

    //********************* ADD DIRECT CONVERSATION ********************************//
    // Appends a newly opened DM partner to the sidebar's "Direct Messages"
    // section. No-op if the user is already listed (idempotent).
    // * userId (string) — GudHub user id of the DM partner
    addDirectConversation(userId) {
        const userIdStr = String(userId);
        if(this.activeConversations.some(c => String(c.user_id) === userIdStr)) return;

        const conversation = { user_id: userIdStr, unread_count: 0 };
        this.activeConversations.unshift(conversation);

        const rowHtml = `<gh-user-side-bar-item class="users_sidebar__user" user-id="${userIdStr}" unread-count="0"></gh-user-side-bar-item>`;
        const container = this.querySelector('.users_sidebar__dm_conversations');
        if(container) container.insertAdjacentHTML('afterbegin', rowHtml);

        const newRow = this.querySelector(`.users_sidebar__dm_conversations gh-user-side-bar-item[user-id="${userIdStr}"]`);
        if(newRow) newRow.setDiscussedItems(this.getDiscussedItems([this.activeUserId, userIdStr]));
    }

    //********************* ADD CHANNEL ********************************//
    // Appends a newly created/joined channel to the sidebar's "Channels"
    // section. Shared by createChannel() and handleEmitToUser's
    // participant_added handling.
    // * conversation (Object) — conversation document (type: 'channel')
    addChannel(conversation) {
        const channel = { conversation_id: conversation.conversation_id, name: conversation.name, unread_count: conversation.unread_count || 0, participants: conversation.participants.map(String) };

        this.channels.push(channel);
        this.appendListItem(this.querySelector('.users_sidebar__channels'), this.renderChannel(channel));

        return channel;
    }

    //********************* APPEND LIST ITEM ********************************//
    // Appends a row to a capped list (see renderCappedList), inserting it
    // before the .users_sidebar__overflow/.users_sidebar__show_more controls
    // if present so newly added rows don't end up after the "Show N more"
    // button.
    // * container (HTMLElement|null)
    // * rowHtml (string)
    appendListItem(container, rowHtml) {
        if(!container) return;

        const anchor = container.querySelector('.users_sidebar__overflow, .users_sidebar__show_more');

        if(anchor) anchor.insertAdjacentHTML('beforebegin', rowHtml);
        else container.insertAdjacentHTML('beforeend', rowHtml);
    }

    //********************* ITEM MATCHES PARTICIPANTS ********************************//
    // True if item.participants includes every "other" id in participantIds -
    // i.e. every id except the active user. This is the shared predicate behind
    // getDiscussedItems/addDiscussedItem for matching an item-thread to a DM row
    // (the other person) or a channel row (its other members).
    // If there are no "other" ids (e.g. a channel where you're currently the
    // only member), falls back to an exact-set match against your own solo
    // threads, so an item you discussed from that channel still shows there.
    // * item (Object) — { participants, ... }
    // * participantIds (Array<string|number>) — [activeUserId, otherUserId] for
    //   a DM, or a channel's participants
    itemMatchesParticipants(item, participantIds) {
        if(!item.participants) return false;

        const itemSet = new Set(item.participants.map(String));
        const requiredSet = new Set(participantIds.map(String));
        const otherIds = [...requiredSet].filter(id => id !== String(this.activeUserId));

        if(otherIds.length > 0) return otherIds.every(id => itemSet.has(id));

        return itemSet.size === requiredSet.size && [...itemSet].every(id => requiredSet.has(id));
    }

    //********************* GET DISCUSSED ITEMS ********************************//
    // Returns the item-threads from this.itemConversations whose participants
    // include the given ids - the "discussed items" for a specific DM partner
    // or channel.
    // * participantIds (Array<string|number>) — [activeUserId, otherUserId] for
    //   a DM, or a channel's participants
    getDiscussedItems(participantIds) {
        return this.itemConversations.filter(item => this.itemMatchesParticipants(item, participantIds));
    }

    //********************* WIRE DISCUSSED ITEMS ********************************//
    // After the initial render, gives each "Direct Messages"/"Channels" row its
    // matching subset of this.itemConversations (see getDiscussedItems), so the
    // row can render its own collapsible "discussed items" list.
    wireDiscussedItems() {
        this.querySelectorAll('.users_sidebar__active_conversations gh-user-side-bar-item[user-id]').forEach(row => {
            row.setDiscussedItems(this.getDiscussedItems([this.activeUserId, row.getAttribute('user-id')]));
        });

        this.querySelectorAll('.users_sidebar__channels gh-channel-side-bar-item[conversation-id]').forEach(row => {
            const channel = this.channels.find(c => c.conversation_id === row.getAttribute('conversation-id'));
            if(channel) row.setDiscussedItems(this.getDiscussedItems(channel.participants));
        });
    }

    //********************* ADD DISCUSSED ITEM ********************************//
    // Appends a newly created/joined item-thread to the "discussed items" list
    // of every Direct Message / Channel row whose participants are included in
    // the item's participants (a single item-thread can be appended to several
    // rows at once - e.g. one per other participant).
    // Called from addItemDiscussion, which in turn is called from
    // handleEmitToUser and gh-direct-message-chat's "Discuss this item" action.
    // * item (Object) — { conversation_id, item_ref, unread_count, participants }
    addDiscussedItem(item) {
        if(!item.participants) return;

        this.querySelectorAll('.users_sidebar__active_conversations gh-user-side-bar-item[user-id]').forEach(row => {
            if(this.itemMatchesParticipants(item, [this.activeUserId, row.getAttribute('user-id')])) row.addDiscussedItem(item);
        });

        this.querySelectorAll('.users_sidebar__channels gh-channel-side-bar-item[conversation-id]').forEach(row => {
            const channel = this.channels.find(c => c.conversation_id === row.getAttribute('conversation-id'));
            if(channel && this.itemMatchesParticipants(item, channel.participants)) row.addDiscussedItem(item);
        });

        this.closest('gudhub-messenger')?.querySelector('gh-direct-message-chat')?.maybeInsertItemDiscussionCard(item);
    }

    //********************* ADD ITEM DISCUSSION ********************************//
    // Adds an item-thread the active user just created/joined to its app's
    // group in the "Item Discussions" list (unless it's already there) and to
    // any DM/Channel rows it matches (see addDiscussedItem). Shared by
    // handleEmitToUser's item_thread_created/joined handling and the
    // item_thread_discussed event (real-time "I was just added as a
    // participant" notifications, including auto-join-on-reply).
    // * item (Object) — { conversation_id, item_ref, unread_count, participants }
    async addItemDiscussion(item) {
        if(this.itemConversations.some(c => c.conversation_id === item.conversation_id)) return;

        this.itemConversations.unshift(item);
        await this.addItemToAppGroup(item);

        const header = this.querySelector('.users_sidebar__item_discussions_header');
        if(header) header.style.display = '';

        // If this is the item-thread for the page the user is currently on
        // (e.g. they just auto-joined it by replying), highlight its new row
        // to match the conversation already shown in .multi-chat.
        if(item.item_ref?.app_id === this.itemContext?.app_id && item.item_ref?.item_id === this.itemContext?.item_id) {
            const row = this.querySelector(`.users_sidebar__item_discussions gh-item-side-bar-item[conversation-id="${item.conversation_id}"]`);
            if(row) this.setActiveElement(row);
            this.renderStartDiscussionBanner(true);
        }

        this.highlightCurrentItem(this.itemContext);
        this.addDiscussedItem(item);
    }

    //********************* ADD ITEM TO APP GROUP ********************************//
    // Inserts a new "Item Discussions" row into its app's group - creating the
    // group, in alphabetically-sorted position, if this is that app's first
    // discussed item - keeping this.itemsByApp and the DOM in sync without a
    // full re-render. Shared by addItemDiscussion's two real-time triggers.
    // * item (Object) — { conversation_id, item_ref, unread_count }
    async addItemToAppGroup(item) {
        const container = this.querySelector('.users_sidebar__item_discussions');
        if(!container) return;

        const appId = String(item.item_ref.app_id);
        const existingGroup = this.itemsByApp.find(group => group.app_id === appId);

        if(existingGroup) {
            existingGroup.items.unshift(item);
            container.querySelector(`.users_sidebar__group[data-app-id="${appId}"] .users_sidebar__group_users`)
                ?.insertAdjacentHTML('afterbegin', this.renderItemDiscussion(item));
            return;
        }

        if(!this.appNamesById.has(appId)) {
            const app = await gudhub.getApp(appId).catch(() => null);
            this.appNamesById.set(appId, app?.app_name || appId);
        }

        const newGroup = { app_id: appId, app_name: this.getAppName(appId), app_icon: await this.renderAppIcon(appId), items: [item], itemsHtml: this.renderItemDiscussion(item) };
        const nextGroup = this.itemsByApp.find(group => group.app_name.localeCompare(newGroup.app_name) > 0);
        this.itemsByApp.splice(nextGroup ? this.itemsByApp.indexOf(nextGroup) : this.itemsByApp.length, 0, newGroup);

        const groupHtml = this.renderItemAppGroup(newGroup, false);
        const nextGroupEl = nextGroup ? container.querySelector(`.users_sidebar__group[data-app-id="${nextGroup.app_id}"]`) : null;

        if(nextGroupEl) {
            nextGroupEl.insertAdjacentHTML('beforebegin', groupHtml);
        } else {
            container.insertAdjacentHTML('beforeend', groupHtml);
        }
    }

    //********************* TOGGLE ITEM APP GROUP ********************************//
    // Expand/collapse for a per-app group in "Item Discussions" - reuses the
    // "Groups" section's own CSS (see renderItemAppGroup) but, unlike
    // toggleGroup(), never needs a lazy-load step since item-discussion data
    // is already fully loaded up front.
    // * element (HTMLElement) — the clicked .users_sidebar__group_label
    toggleItemAppGroup(element) {
        element.closest('.users_sidebar__group').classList.toggle('users_sidebar__group--collapsed');
    }

    //********************* SUBSCRIBE TO CONVERSATIONS ********************************//
    // Subscribes to live '/ws/emit-to-user' pushes addressed to the active user, so
    // unread badges stay live like Slack's channel list, without polling. The push's
    // 'user_id' destination is always the recipient (us) - the sender is identified
    // by data.message.user_id inside the payload, see handleEmitToUser.
    subscribeToConversations() {
        gudhub.on('gudhub_messenger', { user_id: this.activeUserId }, this.handleEmitToUser);
    }

    //********************* UNSUBSCRIBE FROM CONVERSATIONS ********************************//
    // Removes the subscription added by subscribeToConversations().
    unsubscribeFromConversations() {
        gudhub.destroy('gudhub_messenger', { user_id: this.activeUserId }, this.handleEmitToUser);
    }

    //********************* HANDLE EMIT TO USER ********************************//
    // Bumps unread badges when a new message is pushed via '/ws/emit-to-user'
    // for one of the subscribed DM/item-thread/channel conversations, and
    // picks up item-threads/channels the active user was just added to.
    // * error (null) — unused, part of the pipeService callback signature
    // * data (Object) — { conversation_id, message } | { conversation_id, event, item_ref } | { conversation_id, event, user_id }
    handleEmitToUser(error, data) {
        if(data.message) {
            // Non-mentioned users in groups/channels/threads receive silent_message: true —
            // skip unread badge increment so only @mentioned users see the notification.
            if(data.silent_message) return;

            const item = this.itemConversations.find(c => c.conversation_id === data.conversation_id);
            const channel = this.channels.find(c => c.conversation_id === data.conversation_id);

            if(item) {
                item.unread_count = (item.unread_count || 0) + 1;
                this.querySelectorAll(`gh-item-side-bar-item[conversation-id="${data.conversation_id}"]`).forEach(el => el.setUnreadCount(el.unreadCount + 1));
                this.closest('gudhub-messenger')?.querySelector('gh-direct-message-chat')?.incrementItemDiscussionReplyCount(data.conversation_id);
            } else if(channel) {
                channel.unread_count = (channel.unread_count || 0) + 1;
                this.querySelectorAll(`gh-channel-side-bar-item[conversation-id="${data.conversation_id}"]`).forEach(el => el.setUnreadCount(el.unreadCount + 1));
            } else {
                const userId = String(data.message.user_id);
                this.addDirectConversation(userId);
                this.querySelectorAll(`gh-user-side-bar-item[user-id="${userId}"]`).forEach(row => row.setUnreadCount(row.unreadCount + 1));
            }

            return;
        }

        if((data.event === 'item_thread_created' || data.event === 'item_thread_joined') && !this.itemConversations.some(c => c.conversation_id === data.conversation_id)) {
            this.addItemDiscussion({ conversation_id: data.conversation_id, item_ref: data.item_ref, unread_count: 0, participants: (data.participants || []).map(String) });

            return;
        }

        if(data.event === 'participant_added' && String(data.user_id) === String(this.activeUserId)
            && !this.channels.some(c => c.conversation_id === data.conversation_id)
            && !this.itemConversations.some(c => c.conversation_id === data.conversation_id)) {
            this.fetchAndAddConversation(data.conversation_id);
        }
    }

    //********************* FETCH AND ADD CONVERSATION ********************************//
    // Loads a conversation the active user was just added to and adds it to
    // the sidebar - to "Channels" if it's a channel, or to "Item Discussions"
    // (and any matching DM/Channel rows, see addDiscussedItem) if it's an
    // item-thread the active user was just added to via "Add people".
    // * conversationId (string) — conversation_id to fetch
    async fetchAndAddConversation(conversationId) {
        const response = await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/${conversationId}?user_id=${this.activeUserId}`);
        const conversation = await response.json();

        if(conversation.type === 'channel') {
            this.addChannel(conversation);
        } else if(conversation.item_ref) {
            this.addItemDiscussion({
                conversation_id: conversation.conversation_id,
                item_ref: conversation.item_ref,
                unread_count: conversation.unread_count || 0,
                participants: conversation.participants.map(String)
            });
        }
    }

    //********************* MARK CONVERSATION READ ********************************//
    // Clears the unread badge for the DM with the given user, called after
    // gh-direct-message-chat marks that conversation's messages as read.
    // * userId (string) — GudHub user id of the DM partner
    markConversationRead(userId) {
        this.querySelectorAll(`gh-user-side-bar-item[user-id="${userId}"]`).forEach(item => item.setUnreadCount(0));
    }

    //********************* MARK ITEM CONVERSATION READ ********************************//
    // Clears the unread badge for the item-thread with the given conversation
    // id, if it's one of the active user's "Item Discussions". No-op for
    // conversation ids that aren't item-threads (e.g. DMs, message-threads).
    // * conversationId (string) — conversation_id of the item-thread
    markItemConversationRead(conversationId) {
        const item = this.itemConversations.find(c => c.conversation_id === conversationId);
        if(!item) return;

        item.unread_count = 0;
        this.querySelectorAll(`gh-item-side-bar-item[conversation-id="${conversationId}"]`).forEach(el => el.setUnreadCount(0));
    }

    //********************* MARK CHANNEL READ ********************************//
    // Clears the unread badge for the channel with the given conversation id.
    // No-op for conversation ids that aren't channels.
    // * conversationId (string) — conversation_id of the channel
    markChannelRead(conversationId) {
        const channel = this.channels.find(c => c.conversation_id === conversationId);
        if(!channel) return;

        channel.unread_count = 0;
        this.querySelectorAll(`gh-channel-side-bar-item[conversation-id="${conversationId}"]`).forEach(el => el.setUnreadCount(0));
    }

    async toggleGroup(element) {
        const groupElement = element.closest('.users_sidebar__group');
        const wasCollapsed = groupElement.classList.contains('users_sidebar__group--collapsed');

        groupElement.classList.toggle('users_sidebar__group--collapsed');

        if(wasCollapsed) await this.ensureGroupUsersLoaded(groupElement);
    }

    //********************* ENSURE GROUP USERS LOADED ********************************//
    // Lazily loads and renders a group's member list the first time it's
    // expanded (toggleGroup) or searched (toggleSearch). No-op if already
    // loaded. Removes the group row entirely if it has no members.
    // * groupElement (HTMLElement) — .users_sidebar__group row
    async ensureGroupUsersLoaded(groupElement) {
        const group = this.groupedUsers.find(group => group.group_id == groupElement.dataset.groupId);

        if(group.users !== null) return;

        group.users = await this.loadGroupUsers(group.group_id);

        if(!group.users.length) {
            groupElement.remove();
            return;
        }

        group.usersHtml = this.renderUsers(group.users);
        groupElement.querySelector('.users_sidebar__group_users').innerHTML = group.usersHtml;
    }

    //********************* ADD PEOPLE TO GROUP ********************************//
    // "+Add" button in a group's header (admin/owner only, see usersSideBar.html).
    // Opens showAddPeopleToGroupDialog for the group the button belongs to.
    // * element (HTMLElement) — the clicked .users_sidebar__group_add button
    addPeopleToGroup(element) {
        const groupElement = element.closest('.users_sidebar__group');
        const group = this.groupedUsers.find(group => String(group.group_id) === groupElement.dataset.groupId);

        if(group) this.showAddPeopleToGroupDialog(group);
    }

    //********************* SHOW ADD PEOPLE TO GROUP DIALOG ********************************//
    // Opens a GhDialog to add people to a sharing group, styled like the channel
    // "Add people" picker (addPeopleMixin.js) but with a live, account-wide
    // search (gudhub.getUsersList) instead of a preloaded candidate list, since
    // a group's addable pool isn't bounded to people you already share a group
    // with. A search that turns up nobody offers to invite instead: an email
    // query is sent straight away, a name query first explains that an email is
    // needed (see the view state machine below) - same POST /send-invitation
    // mechanism gh_elements/invitation_constructor/invitation_action.js uses.
    // * group (Object) — { group_id, group_name, users, group_permission }
    async showAddPeopleToGroupDialog(group) {
        const self = this;
        const ghDialog = gudhub.ghconstructor.angularInjector.get('GhDialog');

        const existingMembers = await gudhub.groupSharing.getUsersByGroup(group.group_id);
        const existingMemberIds = new Set((existingMembers || []).map(user => String(user.user_id)));

        ghDialog.show({
            position: 'center',
            mobile_position: 'bottom_mobile',
            class: 'webhook_set_dialog add_members_dialog',
            toolbar: false,
            template: {
                toolbar: '',
                content: /*html*/`
                <div class="dialog_wrapper">
                    <span class="add_members_close" ng-click="hide()">&times;</span>

                    <div class="add_members_view" ng-show="view === 'search'">
                        <span class="add_members_back add_members_back--placeholder">&larr; Back</span>
                        <span class="gh_element_name">${group.group_name}</span>
                        <span class="add_members_subtitle">Add Members</span>

                        <div class="add_people_pending" ng-if="pendingUsers.length">
                            <span ng-repeat="user in pendingUsers" class="add_people_chip">
                                {{user.fullname}}
                                <span ng-click="toggleUser(user)" class="add_people_chip__remove">&times;</span>
                            </span>
                        </div>

                        <div class="add_members_search">
                            <input type="text" class="disable-gudhub-styles" ng-model="searchQuery" ng-change="onSearchChange()" placeholder="Search people by name or email">
                        </div>

                        <div class="add_members_list">
                            <div class="add_members_row" ng-repeat="user in searchResults" ng-click="toggleUser(user)" ng-class="{'add_members_row--selected': isUserSelected(user)}">
                                <gh-avatar-webcomponent name="{{user.fullname}}" url="{{user.avatar_128 || user.avatar_512}}"></gh-avatar-webcomponent>
                                <span class="add_members_row__name">{{user.fullname}}</span>
                                <span class="add_members_row__checkbox">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                                </span>
                            </div>
                            <div class="add_members_empty" ng-if="searching">Searching…</div>
                            <div class="add_members_empty" ng-if="!searching && searchQuery.length < ${SEARCH_MIN_LENGTH}">Type at least ${SEARCH_MIN_LENGTH} characters to search</div>
                            <div class="add_members_empty" ng-if="!searching && isInviteMode()">No matching people</div>
                        </div>

                        <span ng-if="errorMessage" class="error">{{errorMessage}}</span>

                        <div class="add_members_actions">
                            <button class="btn btn-blue add_members_action_btn" ng-disabled="showLoader" ng-click="submit()">{{isInviteMode() ? 'Invite' : 'Add'}}</button>
                            <button class="btn btn-blue-reverse add_members_action_btn" ng-click="hide()">Cancel</button>
                        </div>

                        <div ng-if="showLoader" class="create_group_loader loader">
                            <span></span>
                        </div>
                    </div>

                    <div class="add_members_view" ng-show="view === 'invite'">
                        <span class="add_members_back" ng-click="backToSearch()">&larr; Back</span>
                        <span class="gh_element_name">${group.group_name}</span>
                        <span class="add_members_subtitle add_members_subtitle--wrap">We couldn't find "{{searchQuery}}". Enter their email to invite them.</span>

                        <div class="add_members_search">
                            <input type="email" class="disable-gudhub-styles" ng-model="inviteEmail" placeholder="name@example.com">
                        </div>

                        <span ng-if="inviteErrorMessage" class="error">{{inviteErrorMessage}}</span>

                        <div class="add_members_actions">
                            <button class="btn btn-blue add_members_action_btn" ng-disabled="showLoader" ng-click="submitInvite()">Send Invite</button>
                            <button class="btn btn-blue-reverse add_members_action_btn" ng-click="hide()">Cancel</button>
                        </div>

                        <div ng-if="showLoader" class="create_group_loader loader">
                            <span></span>
                        </div>
                    </div>

                    <div class="add_members_view" ng-show="view === 'sent'">
                        <span class="add_members_back add_members_back--placeholder">&larr; Back</span>
                        <span class="gh_element_name">Invitation sent</span>
                        <span class="add_members_subtitle add_members_subtitle--wrap">We sent an invite to {{lastInvitedEmail}}.</span>

                        <div class="add_members_actions">
                            <button class="btn btn-blue add_members_action_btn" ng-click="hide()">Done</button>
                        </div>
                    </div>
                </div>
                `
            },

            controller: ['$scope', function($scope) {
                $scope.view = 'search';
                $scope.pendingUsers = [];
                $scope.searchQuery = '';
                $scope.searchResults = [];
                $scope.searching = false;
                $scope.errorMessage = '';
                $scope.inviteEmail = '';
                $scope.inviteErrorMessage = '';
                $scope.lastInvitedEmail = '';

                let searchTimer = null;
                let searchToken = 0;

                $scope.isUserSelected = (user) => $scope.pendingUsers.some(pending => String(pending.user_id) === String(user.user_id));

                $scope.toggleUser = (user) => {
                    if($scope.isUserSelected(user)) {
                        $scope.pendingUsers = $scope.pendingUsers.filter(pending => String(pending.user_id) !== String(user.user_id));
                    } else {
                        $scope.pendingUsers = [...$scope.pendingUsers, user];
                    }
                };

                // "Invite" always wins over "Add" once the current search has come
                // up empty, even if there are already pending selections - add
                // those first, then search again to invite someone new.
                $scope.isInviteMode = () => {
                    return $scope.searchQuery.trim().length >= SEARCH_MIN_LENGTH && !$scope.searching && !$scope.searchResults.length;
                };

                $scope.onSearchChange = () => {
                    clearTimeout(searchTimer);

                    const query = $scope.searchQuery.trim();

                    if(query.length < SEARCH_MIN_LENGTH) {
                        $scope.searchResults = [];
                        $scope.searching = false;
                        return;
                    }

                    $scope.searching = true;
                    const token = ++searchToken;

                    searchTimer = setTimeout(async () => {
                        const results = await gudhub.getUsersList(query);

                        if(token !== searchToken) return;

                        $scope.searchResults = (results || []).filter(user => !existingMemberIds.has(String(user.user_id)));
                        $scope.searching = false;
                        $scope.$apply();
                    }, SEARCH_DEBOUNCE_MS);
                };

                $scope.backToSearch = () => {
                    $scope.view = 'search';
                    $scope.inviteErrorMessage = '';
                };

                // Shared by both invite paths: a query that's already a valid
                // email (submit() below) and the dedicated 'invite' view's form.
                $scope.sendInvite = async (email) => {
                    $scope.showLoader = true;

                    try {
                        const access_token = await gudhub.getToken();

                        const response = await fetch(`${gudhub.config.node_server_url}/send-invitation`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                to: email,
                                invitedBy: self.activeUserId,
                                groups: [group.group_id],
                                permission: GROUP_MEMBER_PERMISSION,
                                access_token
                            })
                        });

                        const result = await response.json().catch(() => ({}));
                        if(!response.ok || result.status !== 'ok') throw new Error(result.message || 'invite failed');

                        $scope.lastInvitedEmail = email;
                        $scope.view = 'sent';
                    } catch(err) {
                        const message = `Couldn't send the invitation to ${email}. Please try again.`;
                        if($scope.view === 'invite') $scope.inviteErrorMessage = message;
                        else $scope.errorMessage = message;
                    } finally {
                        $scope.showLoader = false;
                    }
                };

                $scope.submitInvite = () => {
                    $scope.inviteErrorMessage = '';

                    const email = $scope.inviteEmail.trim();
                    if(!EMAIL_REGEXP.test(email)) {
                        $scope.inviteErrorMessage = 'Enter a valid email address';
                        return;
                    }

                    $scope.sendInvite(email);
                };

                $scope.submit = async () => {
                    $scope.errorMessage = '';

                    if($scope.isInviteMode()) {
                        const query = $scope.searchQuery.trim();

                        if(EMAIL_REGEXP.test(query)) {
                            $scope.sendInvite(query);
                        } else {
                            $scope.inviteEmail = '';
                            $scope.view = 'invite';
                        }

                        return;
                    }

                    if(!$scope.pendingUsers.length) {
                        $scope.errorMessage = 'Pick at least one person to add';
                        return;
                    }

                    $scope.showLoader = true;

                    const failures = [];
                    const failedUserIds = new Set();

                    for(const user of $scope.pendingUsers) {
                        try {
                            await gudhub.groupSharing.addUserToGroup(group.group_id, user.user_id, GROUP_MEMBER_PERMISSION);
                        } catch(err) {
                            failures.push(user.fullname);
                            failedUserIds.add(String(user.user_id));
                        }
                    }

                    const addedUsers = $scope.pendingUsers.filter(user => !failedUserIds.has(String(user.user_id)));
                    if(addedUsers.length) self.addGroupMembers(group, addedUsers);

                    $scope.showLoader = false;

                    if(failures.length) {
                        $scope.errorMessage = `Couldn't add: ${failures.join(', ')}`;
                        $scope.pendingUsers = $scope.pendingUsers.filter(user => failedUserIds.has(String(user.user_id)));
                        return;
                    }

                    $scope.hide();
                };
            }]
        });
    }

    //********************* ADD GROUP MEMBERS ********************************//
    // Appends newly added members to a group's in-memory user list and
    // re-renders its .users_sidebar__group_users, so showAddPeopleToGroupDialog
    // doesn't require a page reload to reflect the change. No-op for a group
    // whose member list hasn't been loaded yet (it'll be fetched fresh on
    // next expand, see ensureGroupUsersLoaded).
    // * group (Object) — entry from this.groupedUsers
    // * users (Array<Object>) — newly added users, [{ user_id, fullname, ... }]
    addGroupMembers(group, users) {
        if(group.users === null) return;

        group.users = [...group.users, ...users].sort((a, b) => (a.fullname || '').localeCompare(b.fullname || ''));
        group.usersHtml = this.renderUsers(group.users);

        const groupElement = this.querySelector(`.users_sidebar__group[data-group-id="${group.group_id}"]`);
        const usersContainer = groupElement?.querySelector('.users_sidebar__group_users');
        if(usersContainer) usersContainer.innerHTML = group.usersHtml;
    }

    //********************* RENDER USERS ********************************//
    // Markup for a group's member list, capped via renderCappedList.
    // * users (Array<Object>) — [{ user_id, fullname }]
    renderUsers(users) {
        return this.renderCappedList(users, user => `
            <gh-user-side-bar-item class="users_sidebar__user" user-id="${user.user_id}"></gh-user-side-bar-item>
        `);
    }

    //********************* RENDER CAPPED LIST ********************************//
    // Renders at most LIST_LIMIT rows up-front; any remaining rows are wrapped
    // in a hidden .users_sidebar__overflow, followed by a "Show N more" button
    // (see showMore) that reveals them. Returns the rows unchanged if there
    // are LIST_LIMIT or fewer.
    // * items (Array<Object>)
    // * renderRowFn (Function) — item => row HTML string
    renderCappedList(items, renderRowFn) {
        const rows = items.map(renderRowFn);

        if(rows.length <= LIST_LIMIT) return rows.join('');

        const visibleRows = rows.slice(0, LIST_LIMIT).join('');
        const hiddenRows = rows.slice(LIST_LIMIT).join('');

        return `
            ${visibleRows}
            <div class="users_sidebar__overflow">${hiddenRows}</div>
            <button type="button" class="users_sidebar__show_more">
                ${SHOW_MORE_ICON}
                <span>Show ${rows.length - LIST_LIMIT} more</span>
            </button>
        `;
    }

    //********************* SHOW MORE ********************************//
    // Reveals a list's overflow rows (beyond LIST_LIMIT) and removes the
    // "Show N more" button. See renderCappedList.
    // * element (HTMLElement) — the clicked .users_sidebar__show_more button
    showMore(element) {
        element.previousElementSibling.classList.add('users_sidebar__overflow--expanded');
        element.remove();
    }

    //********************* WIRE LIST CONTROLS ********************************//
    // Delegated handlers for the "Show N more" buttons, per-section search
    // toggles/inputs, and Item Discussions' per-app group headers. Delegated
    // on the sidebar itself so they keep working for content rendered later
    // outside a full render() pass - group member lists lazy-loaded via
    // ensureGroupUsersLoaded, and app groups inserted by addItemToAppGroup
    // (unlike toggleGroup()'s onclick attribute, which only gets rewired into
    // a real listener during a full render() pass, and would otherwise stay
    // dead on a group added after the fact - see addItemToAppGroup).
    wireListControls() {
        this.addEventListener('click', (event) => {
            const showMoreButton = event.target.closest('.users_sidebar__show_more');
            if(showMoreButton) {
                this.showMore(showMoreButton);
                return;
            }

            const searchToggle = event.target.closest('.users_sidebar__search_toggle');
            if(searchToggle) {
                this.toggleSearch(searchToggle);
                return;
            }

            const appGroupLabel = event.target.closest('.users_sidebar__item_discussions .users_sidebar__group_label');
            if(appGroupLabel) this.toggleItemAppGroup(appGroupLabel);
        });

        this.addEventListener('input', (event) => {
            if(event.target.classList.contains('users_sidebar__search_input')) this.filterList(event.target);
        });
    }

    //********************* TOGGLE SEARCH ********************************//
    // Toggles a section's search bar, shown via wireListControls when the
    // section's search-toggle button is clicked. Opening the search bar of a
    // collapsed group expands it and loads its members first (see
    // ensureGroupUsersLoaded).
    // * element (HTMLElement) — the clicked .users_sidebar__search_toggle button
    async toggleSearch(element) {
        const header = element.closest('.users_sidebar__section_header, .users_sidebar__group_name');
        const input = header.querySelector('.users_sidebar__search_input');

        if(header.classList.contains('users_sidebar__header--searching')) {
            header.classList.remove('users_sidebar__header--searching');
            input.value = '';
            this.filterList(input);
            return;
        }

        header.classList.add('users_sidebar__header--searching');

        const groupElement = element.closest('.users_sidebar__group');
        if(groupElement) {
            groupElement.classList.remove('users_sidebar__group--collapsed');
            await this.ensureGroupUsersLoaded(groupElement);
        }

        input.focus();
    }

    //********************* FILTER LIST ********************************//
    // Filters a section's rows by name as the user types in its search bar
    // (wired via wireListControls). While a query is active, every matching
    // list's overflow rows (see renderCappedList) are shown and its "Show N
    // more" button hidden, so search isn't limited to the capped rows -
    // plural because the "Item Discussions" section's single search box
    // spans several per-app groups (see renderItemAppGroup), each with its
    // own overflow/show-more pair, unlike every other section's single list.
    // * input (HTMLElement) — .users_sidebar__search_input being typed into
    filterList(input) {
        const header = input.closest('.users_sidebar__section_header, .users_sidebar__group_name');
        const list = header.nextElementSibling;
        const query = input.value.trim().toLowerCase();

        // Only touches an overflow row whose "Show N more" button still
        // exists - a list the user already expanded manually (removing its
        // button, see showMore()) must stay expanded once the query clears.
        list.querySelectorAll('.users_sidebar__show_more').forEach(button => {
            button.style.display = query ? 'none' : '';
            button.previousElementSibling?.classList.toggle('users_sidebar__overflow--expanded', !!query);
        });

        list.querySelectorAll('.users_sidebar__user').forEach(row => {
            const name = row.querySelector('.users_sidebar__user_name')?.textContent.toLowerCase() || '';
            row.classList.toggle('users_sidebar__user--hidden', !!query && !name.includes(query));
        });

        // Only "Item Discussions" nests further .users_sidebar__group elements
        // directly inside the searched list (one per app) - every other
        // searchable list (Direct Messages, Channels, a single shared-users
        // group's own members) has none, so this is a no-op for them.
        list.querySelectorAll(':scope > .users_sidebar__group').forEach(group => {
            const hasMatch = !!group.querySelector('.users_sidebar__user:not(.users_sidebar__user--hidden)');

            if(query) {
                if(group.dataset.wasCollapsed === undefined) {
                    group.dataset.wasCollapsed = group.classList.contains('users_sidebar__group--collapsed') ? 'true' : 'false';
                }
                group.classList.remove('users_sidebar__group--collapsed');
                group.classList.toggle('users_sidebar__group--hidden', !hasMatch);
            } else if(group.dataset.wasCollapsed !== undefined) {
                group.classList.toggle('users_sidebar__group--collapsed', group.dataset.wasCollapsed === 'true');
                group.classList.remove('users_sidebar__group--hidden');
                delete group.dataset.wasCollapsed;
            }
        });
    }

    //********************* TOGGLE CONVERSATIONS PANEL ********************************//
    // Delegates to the parent gudhub-messenger panel's expand/collapse toggle.
    toggleConversationsPanel() {
        this.closest('gudhub-messenger')?.toggleConversationsPanel();
    }

    //********************* OPEN PHONE ********************************//
    // Desktop entry point for gh-call-profile - delegates to openPhoneForUser
    // with no explicit user, which falls back to the active DM (or the
    // contacts picker if none is open).
    openPhone() {
        this.openPhoneForUser();
    }

    //********************* OPEN PHONE FOR USER ********************************//
    // Shared by openPhone() and a sidebar row's hover phone icon
    // (gh-user-side-bar-item). Highlights the phone button the same way
    // selectActivity() highlights itself - gh-call-profile opening doesn't
    // otherwise notify the sidebar, so this is the only place that clears
    // whichever of Activity/a conversation row was active before. Delegates
    // to showPhoneTab(), which shows the given user's profile, or falls back
    // to the active DM/contacts picker when no user is passed.
    // * user (Object, optional) - user whose profile to show directly.
    openPhoneForUser(user) {
        this.activeView = 'phone';

        this.querySelectorAll('.users_sidebar__user--active').forEach(el => el.classList.remove('users_sidebar__user--active'));
        this.querySelector('.users_sidebar__activity_btn')?.classList.remove('users_sidebar__activity_btn--active');
        this.querySelector('.users_sidebar__email_btn')?.classList.remove('users_sidebar__email_btn--active');
        this.querySelector('.users_sidebar__phone_btn')?.classList.add('users_sidebar__phone_btn--active');

        this.closest('gudhub-messenger')?.showPhoneTab(user);
    }

    //********************* SELECT MESSENGER ********************************//
    // Switches .multi-chat to the "Messengers" view set to the given external
    // messenger, and highlights its row in the sidebar.
    // * element (HTMLElement) — the clicked messenger row
    // * index (number) — index into model.data_model.messengers / .messenger-select
    selectMessenger(element, index) {
        this.activeView = 'messengers';
        this.setActiveElement(element);

        this.dispatchEvent(new CustomEvent('switch_chat_view', {
            bubbles: true,
            composed: true,
            detail: { view: 'messengers', messenger_index: index }
        }));
    }

    //********************* SELECT DIRECT MESSAGE ********************************//
    // Switches .multi-chat to a GudHub Messenger DM with the given user and
    // highlights that user's row in the sidebar.
    // * element (HTMLElement) — the clicked gh-user-side-bar-item
    // * userId (string) — GudHub user id of the selected user
    selectDirectMessage(element, userId) {
        this.activeView = 'direct_message';
        this.setActiveElement(element);
        element.setUnreadCount(0);

        this.dispatchEvent(new CustomEvent('switch_chat_view', {
            bubbles: true,
            composed: true,
            detail: { view: 'direct_message', user_id: userId }
        }));
    }

    //********************* SET ACTIVE ELEMENT ********************************//
    // Moves the --active highlight to the given sidebar row, removing it from
    // any previously active row.
    // * element (HTMLElement) — the row to mark as active
    setActiveElement(element) {
        this.querySelector('.users_sidebar__user--active')?.classList.remove('users_sidebar__user--active');
        element.classList.add('users_sidebar__user--active');
    }

    //********************* SELECT ITEM DISCUSSION ********************************//
    // Switches .multi-chat to an item-thread and highlights its row in the
    // sidebar.
    // * element (HTMLElement) — the clicked gh-item-side-bar-item
    // * conversationId (string) — conversation_id of the item-thread
    // * itemRef (Object) — { app_id, item_id, view_id }
    selectItemDiscussion(element, conversationId, itemRef) {
        this.activeView = 'item_discussion';
        this.setActiveElement(element);
        element.setUnreadCount(0);

        this.dispatchEvent(new CustomEvent('switch_chat_view', {
            bubbles: true,
            composed: true,
            detail: { view: 'item_discussion', conversation_id: conversationId, item_ref: itemRef }
        }));
    }

    //********************* SELECT CHANNEL ********************************//
    // Switches .multi-chat to a channel conversation and highlights its row
    // in the sidebar.
    // * element (HTMLElement) — the clicked gh-channel-side-bar-item
    // * conversationId (string) — conversation_id of the channel
    // * name (string) — channel name
    selectChannel(element, conversationId, name) {
        this.activeView = 'channel';
        this.setActiveElement(element);
        element.setUnreadCount(0);

        this.dispatchEvent(new CustomEvent('switch_chat_view', {
            bubbles: true,
            composed: true,
            detail: { view: 'channel', conversation_id: conversationId, name }
        }));
    }

    //********************* CREATE CHANNEL ********************************//
    // "+" action in the "Channels" header. Opens a GhDialog asking for a
    // channel name, creates the conversation, adds it to the sidebar, and
    // opens it.
    createChannel() {
        const self = this;
        const ghDialog = gudhub.ghconstructor.angularInjector.get('GhDialog');

        ghDialog.show({
            position: 'center_event_position',
            mobile_position: 'bottom_mobile',
            class: 'webhook_set_dialog create_channel_dialog',
            toolbar: false,
            template: {
                toolbar: '',
                content: /*html*/`
                <div class="dialog_wrapper">
                    <span class="create_channel_close" ng-click="hide()">&times;</span>
                    <span class="create_channel_title">Create Channel</span>

                    <div class="field_wrapper">
                        <span class="field-wrap-name"><span class="gh_element_name">Channel Name</span></span>
                        <gh-input gh-editable="true" gh-data-type="text" type="text" ng-model="channelName" class="group_name"></gh-input>
                    </div>

                    <span ng-if="showErrorMessage" class="error"> Please enter a channel name </span>

                    <button class="btn save_btn btn-grean" ng-click="createChannel()"> Create Channel </button>

                    <div ng-if="showLoader" class="create_group_loader loader">
                        <span></span>
                    </div>
                </div>
                `
            },

            controller: ['$scope', function($scope) {
                $scope.createChannel = async () => {
                    $scope.showErrorMessage = false;

                    if(!$scope.channelName) {
                        $scope.showErrorMessage = true;
                        return;
                    }

                    $scope.showLoader = true;

                    const response = await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/create`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            type: 'channel',
                            name: $scope.channelName,
                            participants: [self.activeUserId],
                            created_by: self.activeUserId
                        })
                    });

                    const conversation = await response.json();

                    $scope.showLoader = false;
                    $scope.hide();

                    const channel = self.addChannel(conversation);
                    self.selectChannel(self.querySelector(`gh-channel-side-bar-item[conversation-id="${channel.conversation_id}"]`), channel.conversation_id, channel.name);
                }
            }]
        });
    }

    //********************* FOCUS CONVERSATION ********************************//
    // Called by gh_conversation.js when a switch_chat_view event comes from
    // outside the sidebar (e.g. clicking an activity feed item). Finds the
    // matching sidebar row and moves the --active highlight to it.
    // * detail (Object) — switch_chat_view event detail
    focusConversation({ view, user_id, conversation_id }) {
        let element = null;

        if(view === 'direct_message' && user_id) {
            element = this.querySelector(`gh-user-side-bar-item[user-id="${user_id}"]`);
            if(element) this.activeView = 'direct_message';
        } else if(view === 'channel' && conversation_id) {
            element = this.querySelector(`gh-channel-side-bar-item[conversation-id="${conversation_id}"]`);
            if(element) this.activeView = 'channel';
        } else if(view === 'item_discussion' && conversation_id) {
            // Prefer the flat "Item Discussions" list over nested discussed-items
            // rows inside DM rows — both can share the same conversation-id, and
            // the flat entry comes later in the DOM so querySelector finds the
            // nested one first, which may be hidden inside a collapsed DM row.
            element = this.querySelector(`.users_sidebar__item_discussions gh-item-side-bar-item[conversation-id="${conversation_id}"]`)
                   ?? this.querySelector(`gh-item-side-bar-item[conversation-id="${conversation_id}"]`);
            if(element) this.activeView = 'item_discussion';
        }

        if(element) {
            this.querySelector('.users_sidebar__activity_btn')?.classList.remove('users_sidebar__activity_btn--active');
            this.querySelector('.users_sidebar__phone_btn')?.classList.remove('users_sidebar__phone_btn--active');
            this.querySelector('.users_sidebar__email_btn')?.classList.remove('users_sidebar__email_btn--active');
            this.setActiveElement(element);
        }
    }

    //********************* SELECT ACTIVITY ********************************//
    // Opens the activity feed in .multi-chat and highlights the activity button.
    // Expands the panel if it is currently collapsed.
    selectActivity() {
        this.activeView = 'activity';

        this.querySelectorAll('.users_sidebar__user--active').forEach(el => el.classList.remove('users_sidebar__user--active'));
        this.querySelector('.users_sidebar__phone_btn')?.classList.remove('users_sidebar__phone_btn--active');
        this.querySelector('.users_sidebar__email_btn')?.classList.remove('users_sidebar__email_btn--active');
        this.querySelector('.users_sidebar__activity_btn')?.classList.add('users_sidebar__activity_btn--active');

        this.closest('gudhub-messenger')?.setExpanded(true);

        this.dispatchEvent(new CustomEvent('switch_chat_view', {
            bubbles: true,
            detail: { view: 'activity' }
        }));
    }

    //********************* SELECT EMAIL ********************************//
    // Opens the email inbox in .multi-chat and highlights the email button.
    // Expands the panel if it is currently collapsed. Mirrors selectActivity().
    selectEmail() {
        this.activeView = 'email';

        this.querySelectorAll('.users_sidebar__user--active').forEach(el => el.classList.remove('users_sidebar__user--active'));
        this.querySelector('.users_sidebar__activity_btn')?.classList.remove('users_sidebar__activity_btn--active');
        this.querySelector('.users_sidebar__phone_btn')?.classList.remove('users_sidebar__phone_btn--active');
        this.querySelector('.users_sidebar__email_btn')?.classList.add('users_sidebar__email_btn--active');

        this.closest('gudhub-messenger')?.setExpanded(true);

        this.dispatchEvent(new CustomEvent('switch_chat_view', {
            bubbles: true,
            detail: { view: 'email' }
        }));
    }

    //********************* SET ITEM CONTEXT ********************************//
    // Called by gudhub-messenger on $routeChangeSuccess with the item the user
    // is currently viewing (or null), so the sidebar can highlight/auto-open
    // its discussion thread if one exists.
    // * itemContext (Object|null) — { app_id, view_id, item_id } | null
    setItemContext(itemContext) {
        this.itemContext = itemContext;

        this.highlightCurrentItem(itemContext);

        if(itemContext) {
            this.applyItemDiscussionState(itemContext);
            return;
        }

        this.renderStartDiscussionBanner(false);

        // Leaving the item route entirely (as opposed to navigating between
        // items - see applyItemDiscussionState) - fall back to Activity, same
        // as the initial-load default in onInit, unless the user has an
        // actual dm/channel open (gh-direct-message-chat.setItemContext
        // already cleared/hid any standalone item-thread or "no discussion
        // yet" composer by this point, so a real conversation is the only
        // thing left worth not disrupting).
        const dmChat = this.closest('gudhub-messenger')?.querySelector('gh-direct-message-chat');
        const hasOpenConversation = dmChat && !dmChat.classList.contains('hidden') && (dmChat.viewMode === 'dm' || dmChat.viewMode === 'channel');

        if(!hasOpenConversation && !this.hasMessengers && !window.matchMedia('(max-width: 767px)').matches) {
            this.dispatchEvent(new CustomEvent('switch_chat_view', {
                bubbles: true,
                detail: { view: 'activity' }
            }));
        }
    }

    //********************* APPLY ITEM DISCUSSION STATE ********************************//
    // Shared by onInit, setItemContext, and gudhub-messenger's mobile
    // showChatTab - looks up whether the current item already has a
    // discussion (auto-opening it if so, see maybeAutoOpenItemDiscussion),
    // updates the "Start discussion" top-panel button accordingly, and - if
    // there's no discussion yet - switches .multi-chat to the empty "type a
    // message to start" composer (see showNoDiscussionState).
    // * itemContext (Object) — { app_id, view_id, item_id }
    // * options.respectOpenConversation (boolean, default true) — when true
    //   (passive route-change callers: onInit/setItemContext), leaves an
    //   already-open dm/channel alone instead of switching to the composer,
    //   so browsing between item pages doesn't yank the user out of an
    //   active conversation. showChatTab passes false - the Chat tab is an
    //   explicit "take me to chat" tap that always lands on the item's own
    //   view when one is in context, same as it already does for an
    //   existing thread.
    async applyItemDiscussionState(itemContext, { respectOpenConversation = true } = {}) {
        const opened = await this.maybeAutoOpenItemDiscussion(itemContext);
        if(this.itemContext !== itemContext) return;

        this.renderStartDiscussionBanner(opened);

        if(opened) return;

        if(respectOpenConversation) {
            const dmChat = this.closest('gudhub-messenger')?.querySelector('gh-direct-message-chat');
            if(dmChat && !dmChat.classList.contains('hidden')) return;
        }

        this.showNoDiscussionState(itemContext);
    }

    //********************* SHOW NO DISCUSSION STATE ********************************//
    // Switches .multi-chat to gh-direct-message-chat's empty "no discussion
    // yet" composer (see conversationMixin.js's showNoDiscussion), so the
    // user can start one by simply typing a message and sending it (see
    // messagingMixin.js's sendMessage). Called from applyItemDiscussionState
    // when the current item has no thread and no dm/channel is already open.
    // * itemContext (Object) — { app_id, view_id, item_id }
    showNoDiscussionState(itemContext) {
        this.activeView = 'no_discussion';
        this.querySelectorAll('.users_sidebar__user--active').forEach(el => el.classList.remove('users_sidebar__user--active'));

        this.dispatchEvent(new CustomEvent('switch_chat_view', {
            bubbles: true,
            composed: true,
            detail: { view: 'no_discussion', item_ref: itemContext }
        }));
    }

    //********************* HIGHLIGHT CURRENT ITEM ********************************//
    // Marks every row for the item the user is currently viewing (if they've
    // discussed it) with users_sidebar__user--current_item - its row in "Item
    // Discussions" as well as any duplicate rows in DM/Channel "discussed
    // items" sublists.
    // * itemContext (Object|null) — { app_id, item_id, view_id } | null
    highlightCurrentItem(itemContext) {
        this.querySelectorAll('.users_sidebar__user--current_item').forEach(el => el.classList.remove('users_sidebar__user--current_item'));

        if(!itemContext || !this.itemConversations) return;

        const item = this.itemConversations.find(c => c.item_ref?.app_id === itemContext.app_id && c.item_ref?.item_id === itemContext.item_id);
        if(!item) return;

        this.querySelectorAll(`gh-item-side-bar-item[conversation-id="${item.conversation_id}"]`).forEach(el => el.classList.add('users_sidebar__user--current_item'));
    }

    //********************* CAN AUTO SWITCH ********************************//
    // Always true - maybeAutoOpenItemDiscussion only ever runs as a direct
    // result of the user themselves navigating to an item's page, so
    // switching .multi-chat to that item's discussion (replacing whatever
    // DM/channel/other item's thread was showing) is always the wanted
    // outcome, not a surprise interruption.
    canAutoSwitch() {
        return true;
    }

    //********************* MAYBE AUTO OPEN ITEM DISCUSSION ********************************//
    // Called from setItemContext when navigating to an item page, and from
    // gudhub-messenger's mobile showChatTab() to decide between landing on
    // the item's discussion vs. the sidebar list. If that item already has a
    // discussion thread - whether or not the active user has joined it yet -
    // silently preloads it into .multi-chat (see canAutoSwitch). Read-only
    // until the user replies: this never adds a participant, matching the
    // existing reply-triggers-join behavior in addMessage().
    // * itemContext (Object) — { app_id, view_id, item_id }
    // Returns true if a discussion was found and switched to, false otherwise
    // - callers use this to decide whether they need their own fallback view.
    async maybeAutoOpenItemDiscussion(itemContext) {
        if(!this.canAutoSwitch() || !this.itemConversations) return false;

        let item = this.itemConversations.find(c => c.item_ref?.app_id === itemContext.app_id && c.item_ref?.item_id === itemContext.item_id);

        if(!item) {
            const response = await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/item?app_id=${itemContext.app_id}&item_id=${itemContext.item_id}&user_id=${this.activeUserId}`);
            if(!response.ok) return false;

            const text = await response.text();
            if(!text) return false;
            const conversation = JSON.parse(text);
            if(this.itemContext !== itemContext || !this.canAutoSwitch()) return false;

            item = { conversation_id: conversation.conversation_id, item_ref: conversation.item_ref };
        }

        this.activeView = 'item_discussion';

        const row = this.querySelector(`.users_sidebar__item_discussions gh-item-side-bar-item[conversation-id="${item.conversation_id}"]`);
        if(row) this.setActiveElement(row);

        this.dispatchEvent(new CustomEvent('switch_chat_view', {
            bubbles: true,
            composed: true,
            detail: { view: 'item_discussion', conversation_id: item.conversation_id, item_ref: item.item_ref }
        }));

        return true;
    }

    //********************* RENDER START DISCUSSION BANNER ********************************//
    // Shows/hides the "Start discussion" button in the top panel - the entry
    // point for creating a solo item-thread on the current item without
    // needing a contact/DM/channel first (see startItemDiscussion). Hidden
    // whenever there's no item context, or the item already has a discussion
    // thread (whether or not the active user has joined it - maybeAutoOpenItemDiscussion
    // already switched .multi-chat to it in that case).
    // * hasDiscussion (boolean) — true if the current item already has a thread
    renderStartDiscussionBanner(hasDiscussion) {
        const button = this.querySelector('.users_sidebar__start_discussion_btn');
        if(!button) return;

        button.classList.toggle('hidden', !this.itemContext || !!hasDiscussion);
    }

    //********************* START ITEM DISCUSSION ********************************//
    // "Start discussion" banner button - creates a solo item-thread (just the
    // active user, no contact required) for the current item via the same
    // find-or-create endpoint "Discuss this item" uses, then opens it.
    async startItemDiscussion() {
        const { app_id, item_id, view_id } = this.itemContext;

        const response = await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/item`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ app_id, item_id, view_id, participants: [], created_by: this.activeUserId })
        });

        const conversation = await response.json();

        await this.addItemDiscussion({
            conversation_id: conversation.conversation_id,
            item_ref: conversation.item_ref,
            unread_count: 0,
            participants: conversation.participants.map(String)
        });

        const row = this.querySelector(`.users_sidebar__item_discussions gh-item-side-bar-item[conversation-id="${conversation.conversation_id}"]`);
        if(row) this.selectItemDiscussion(row, conversation.conversation_id, conversation.item_ref);
    }
}

if(!customElements.get('gh-users-side-bar')){
    customElements.define('gh-users-side-bar', GhUsersSideBar);
}
